import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import AnamnesiTab from './AnamnesiTab';
import PazienteForm from './PazienteForm';
import ProcedureMediche from './ProcedureMediche';
import AlertTab from './AlertTab';
import FotoTab from './FotoTab';
import SchedeProfilo from './SchedeProfilo';
import RiquadroAbitudini from './RiquadroAbitudini';
import RegistroRx from './RegistroRx';
import DocumentiTab from './DocumentiTab';
import NotaInline from './NotaInline';
import CompletamentoConsensi from './CompletamentoConsensi';
import LongevityTab from './LongevityTab';
import ProfiloAlimentareTab from './ProfiloAlimentareTab';
import ElencoQuestionari from './ElencoQuestionari';
import AndamentoPeso from './AndamentoPeso';
import EsamiRefertiTab from './EsamiRefertiTab';
import FatturazioneTab from './FatturazioneTab';
import RiquadroLavoro from './RiquadroLavoro';
import RiepilogoCartella, { dataPunti } from './RiepilogoCartella';
import { useFinestraIndietro } from '../../utils/finestraIndietro';
import { areeProfilo } from './areeProfilo';
import { pazientiAPI, anamnesiVociAPI, visiteAPI, alertAPI, fotoAPI, rxAPI } from '../../services/api';
import { pacchettiAPI } from '../../services/fatturazioneApi';
import { TriangleAlert, Droplet, Mail, Phone } from 'lucide-react';
import '../../styles/SchedaPazienteConsole.css';
import ModulisticaWizard from './ModulisticaWizard';
import { useAuthStore } from '../../store/useStore';
import { puoModificareAnagrafica } from '../../utils/permessi';
import SceltaFirma from './SceltaFirma';
import api from '../../services/api';
import { polarAPI } from '../../services/polarApi';
import { apriEmail } from '../../utils/posta';
import { confermaUscita, confermaUscitaSalvando } from '../../utils/uscitaProtetta';
import { ascolta } from '../../utils/annunci';
import { apriWhatsApp } from '../../utils/recapiti';
import IconaWhatsApp from '../comuni/IconaWhatsApp';
import RefertiScritti from './RefertiScritti';
import { notificaErrore, notificaAvviso, messaggioDa } from '../../utils/notifiche';
import { conUnTentativo, motivoDellaLettura } from '../../utils/primaLettura';
import { esitoLetturaCartella } from './letturaCartella';
import SegnoOpposizioneTs from '../SegnoOpposizioneTs';
import CaricaDalTelefono from './CaricaDalTelefono';
import LinguetteCartella from './LinguetteCartella';
import { avvisiDellaTestata } from './avvisiTestata';
import SegniAvvisi from './SegniAvvisi';
import { useTelefono, vistaTelefonoDelMedico } from '../../navigazione/telefono';

/* ------------------------------------------------------------------
   LA CARTELLA NELLO STILE «CONSOLE» (Alex, 6/10/2026; mockup «Paziente»,
   docs/proposte/26-stile-console.md, fase 2, schermata 3).

   In testa l'INTESTAZIONE, uguale su tutte le linguette e ferma mentre la
   pagina scorre (sui monitor della clinica; sotto gli 820 px di larghezza o
   i 700 di altezza scorre con la pagina, perche' li' toglierebbe lo spazio
   al lavoro): chi e' il paziente, «Appuntamento» e «Nuova visita», e tre
   segni da sapere prima di toccarlo - i consensi firmati, il pacchetto
   aperto, le allergie. Sotto le LINGUETTE, coi loro numeri. Fino al 6/10
   erano undici quadrati con un'icona e il nome sotto, a parole solo al
   passaggio del mouse.

   COSA NON E' CAMBIATO, ed e' voluto:
   - la linguetta sta nell'indirizzo (?vista=, ?sotto=), come dal 28/09;
   - le letture all'apertura sono quelle di prima, piu' due che non lasciano
     traccia nel registro degli accessi (i pacchetti del paziente e, sul
     riepilogo, i prossimi appuntamenti): la cartella e' fra le pagine con
     traccia all'apertura (PAGINE_CON_TRACCIA), e le righe che scrive
     aprendosi restano quelle di prima;
   - la riservatezza la decide il server: le visite arrivano gia' ridotte
     per chi non le vede, e i pacchetti riservati nella forma ridotta;
   - le classi che le prove cercano restano dove fanno la stessa cosa:
     .sp-header col nome in .sp-name, le linguette .sp-iconbtn col loro
     title (cambia l'aspetto, non il gesto), il riepilogo .sp-recap, il
     riquadro dei dati del paziente con le sue righe .sp-row.
   ------------------------------------------------------------------ */

/* Le iniziali del riquadro accanto al nome, come nel mockup. */
const iniziali = (p) => `${String(p.nome || '').trim().charAt(0)}${String(p.cognome || '').trim().charAt(0)}`
    .toUpperCase();

/* IL TELEFONO IN TESTA E' INTERO, com'e' scritto nella scheda (Alex,
   2/11/2026: «Il numero qui deve essere tutto visibile»). Fino a quel giorno
   era mascherato come nel mockup («333 •••• 412»), una scelta dello stile mai
   decisa da Alex; lo stile di prima lo ha sempre mostrato intero. */

/* Una risposta diventa una lettura: { elenco } se porta l'elenco, altrimenti
   { errore: true }. Una risposta senza l'elenco non e' un elenco vuoto. */
/* `dati` e' la risposta intera (voci e risposte si'/no): gli avvisi della
   testata li decide avvisiDellaTestata, la stessa funzione dello stile di prima. */
const letturaVoci = (r) => (Array.isArray(r?.data?.data?.voci)
  ? { elenco: r.data.data.voci, dati: r.data.data } : { errore: true });
const letturaVisite = (r) => (r?.data?.success && Array.isArray(r.data.data?.visite)
    ? { elenco: r.data.data.visite } : { errore: true });

/* ------------------------------------------------------------------ */

const PazienteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [paziente, setPaziente] = useState(null);
  /* La prima lettura della cartella non e' arrivata (vedi loadPaziente). */
  const [letturaFallita, setLetturaFallita] = useState(null);
  const [loading, setLoading] = useState(true);
  /* LE LETTURE CHE DICONO «NESSUNO» HANNO TRE STATI (revisione del
     6/10/2026): `null` finche' non arrivano, { elenco } quando sono
     arrivate, { errore: true } quando la rotta non ha risposto. Fino a quel
     giorno un errore lasciava l'elenco vuoto, e il segno in testa diceva
     «Allergie: nessuna segnalata» a un paziente con quattro allergie attive
     (misurato sul banco con la lettura dell'anamnesi a 500), il riepilogo
     «Nessuna terapia in corso.» e «Nessuna visita registrata.». Un elenco
     vuoto e una lettura non riuscita sono due esiti diversi (CLAUDE.md, «il
     risultato vuoto e' un esito da distinguere»), e qui si scrivono diversi.
     - `anamnesi`: le voci dell'anamnesi. Ne escono le allergie del segno in
       testa e le terapie del riepilogo.
     - `visite`: l'elenco delle visite, per il riepilogo e il numero della
       linguetta. */
  const [anamnesi, setAnamnesi] = useState(null);
  const [visite, setVisite] = useState(null);
  /* Chi arriva dall'agenda dopo aver trasformato un appuntamento in visita
     vuole le procedure mediche, non la home della scheda: la schermata di
     partenza si passa nello state della navigazione, senza aggiungere una
     rotta ne' toccare chi apre la scheda normalmente. */
  const { state: statoNavigazione } = useLocation();
  /* LA LINGUETTA STA NELL'INDIRIZZO (Alex, 28/09/2026): ?vista=procedure,
     ?vista=profilo&sotto=anamnesi, ?vista=incassi. Prima era uno stato del
     componente, e «Indietro» del browser o del mouse usciva dal paziente
     invece di tornare alla linguetta di prima; ricaricare riportava
     all'anagrafica. La navigazione che arriva con la schermata nello state
     (l'agenda, un promemoria) resta: la prima volta la si porta
     nell'indirizzo, senza aggiungere un passo alla cronologia. */
  const [parametri, setParametri] = useSearchParams();
  const schermata = parametri.get('vista') || statoNavigazione?.schermata || 'home';
  const scriviIndirizzo = (modifiche, sostituisci = false) => setParametri((p) => {
    const n = new URLSearchParams(p);
    Object.entries(modifiche).forEach(([k, v]) => (v === null || v === undefined || v === '' ? n.delete(k) : n.set(k, String(v))));
    return n;
  }, { replace: sostituisci });
  const setSchermataNuda = (s) => scriviIndirizzo({
    vista: s === 'home' ? null : s,
    sotto: s === 'profilo' ? (parametri.get('sotto') || null) : null,
    visita: null,
  });
  useEffect(() => {
    if (!parametri.get('vista') && statoNavigazione?.schermata && statoNavigazione.schermata !== 'home') {
      scriviIndirizzo({ vista: statoNavigazione.schermata, sotto: statoNavigazione.sottoProfilo || null }, true);
    }
    // eslint-disable-next-line
  }, []);
  /* Le linguette sono stato locale, non URL: il router non vede il
     cambio e useBlocker non puo' fermarlo. Prima di smontare una
     linguetta con un modulo sporco dentro si chiede qui, con la stessa
     domanda (utils/uscitaProtetta.js). Restare sulla stessa linguetta
     non e' un'uscita. */
  /* PRIMA SI SALVA (Alex, 14/10/2026): cambiando linguetta con un campo
     scritto o una scrittura ancora in volo, il gestionale aspetta il
     salvataggio e chiede solo se fallisce o non finisce in tempo
     (confermaUscitaSalvando: il tempo e le ragioni sono in uscitaProtetta.js).
     Come in PazienteDetail.jsx, la cartella di prima. */
  const setSchermata = (s) => {
    if (s === schermata) { setSchermataNuda(s); return; }
    confermaUscitaSalvando().then((si) => { if (si) setSchermataNuda(s); });
  };
  /* Quando si arriva da un promemoria che indica una visita precisa, la
     scheda non deve solo aprire le procedure: deve aprire QUELLA visita.
     Il valore si consuma una volta sola — passando a un'altra schermata e
     tornando indietro l'elenco resta chiuso, come se ci si fosse arrivati
     a mano, che e' cio' che l'operatore si aspetta. */
  const [visitaDaAprire, setVisitaDaAprire] = useState(statoNavigazione?.visitaId || null);
  /* «Nuova visita» in testa e «+ Aggiungi» delle terapie nel riepilogo:
     portano alla loro linguetta e chiedono LO STESSO gesto del pulsante che
     sta li' (VisiteTab, TerapieInCorso). Si consumano una volta. */
  const [nuovaVisitaRichiesta, setNuovaVisitaRichiesta] = useState(0);
  const [terapiaDaAggiungere, setTerapiaDaAggiungere] = useState(false);
  /* La linguetta Longevity la vede chi ha la casella «percorso longevity»
     sulla propria scheda medico, piu' amministrazione e segreteria. Chi
     decide e' il server: qui si chiede e basta. Nasconderla non e' la
     protezione - quella sta sulle rotte - e' solo il non mettere in menu
     una cosa che poi risponderebbe 403. */
  const [vedeLongevity, setVedeLongevity] = useState(false);
  /* Gli incassi e la fatturazione: tutte le loro rotte rispondono solo ad
     amministrazione e segreteria (fatturazioneRoutes). Fino al 25
     settembre 2026 la linguetta si vedeva anche dai medici, che a ogni
     apertura prendevano tre 403. La protezione resta sul server; qui si
     smette di mostrare una porta chiusa. */
  const utente = useAuthStore((st) => st.user);
  const ruolo = utente?.ruolo;
  /* «Scatta» e «Carica» in fondo alla scheda: solo il medico al telefono
     (stile nuovo, fino a 480 px, collegato a un medico; proposta
     telefono-stile-nuovo, punto 1). Le rotte sono quelle di sempre. */
  const schermoTelefono = useTelefono();
  const caricaDalTelefono = vistaTelefonoDelMedico(utente, schermoTelefono);
  const vedeIncassi = ruolo === 'admin' || ruolo === 'segreteria';
  /* L'anagrafica la modificano segreteria, amministrazione (Alex, 12/10/2026,
     domanda 18) e il medico senior (Alex, 13/10/2026). Al medico semplice la
     scheda mostra i dati e basta, senza «Modifica dati»: il server
     rifiuterebbe con un 403. Per ruolo, non per collegamento a un medico. */
  const scriveAnagrafica = puoModificareAnagrafica(ruolo);
  /* Completare e rifirmare la pratica, e completare i consensi del punto
     prelievi, e' un lavoro di segreteria e amministrazione. Chi puo' farlo lo
     dice il server, con la scheda (fa_pratiche, dalla stessa regola delle
     rotte: services/praticaCompleta, RUOLI_PRATICA): qui non se ne tiene una
     copia. Nel dubbio no: fino alla risposta nessun pulsante e nessuna
     finestra (revisione del beta, 7/10/2026). */
  const [faPratiche, setFaPratiche] = useState(false);
/* «Nuova visita» la vede chi e' collegato a un medico: le visite le
     registra solo lui (MSG_SOLO_MEDICO, visiteController), ed e' la stessa
     condizione del pulsante nell'elenco delle visite. Il pulsante nascosto
     e' informazione, non protezione: il server rifiuta comunque. */
  const registraVisite = !!utente?.medico_id;
  /* Il registro RX: l'abilitazione sta sul MEDICO, non sul ruolo, e la
     decide amministrazione o segreteria. Qui si chiede e basta: la
     protezione e' su ogni rotta del registro. */
  const [vedeRx, setVedeRx] = useState(false);
  /* Le aree delle schede cliniche che questo utente deve vedere nel
     profilo medico. `null` finche' la risposta non arriva: con un elenco
     vuoto di partenza le voci lampeggerebbero via e poi tornerebbero. */
  const [areeSchede, setAreeSchede] = useState(null);
  /* I codici delle schede del catalogo che si compilano nel profilo di
     QUESTO paziente. Vuoto di partenza e non `null`: qui il dubbio si
     risolve al contrario delle aree - una voce che compare e poi dice
     «scheda non disponibile» e' peggio di una voce che appare un istante
     dopo. */
  const [schedeProfilo, setSchedeProfilo] = useState([]);
  /* Gli avvisi che vengono dalle schede cliniche: una spunta accesa su un
     campo che porta un allarme. Stanno accanto alle allergie perche' sono
     la stessa cosa per chi legge - qualcosa che va saputo prima di
     toccare il paziente - anche se arrivano da un'altra parte. */
  const [avvisiSchede, setAvvisiSchede] = useState([]);
  // Voci del consenso privacy: da li' escono le persone designate
  const [designate, setDesignate] = useState(null);
  /* I consensi firmati, per il segno «Consensi 2/3 firmati» in testa: i tipi
     firmati li dice l'elenco dei consensi attivi, su quanti si conta lo dice
     il server (`tipi`, i tipi che la tabella ammette). `null` finche' non
     arriva: un segno «0/3» per un istante sarebbe un falso allarme. */
  const [consensi, setConsensi] = useState(null);
  /* I pacchetti APERTI del paziente: quali lo dice il server (`aperto`,
     pacchetti_aperti), nella forma ridotta per chi non li vede per intero. */
  const [pacchettiAperti, setPacchettiAperti] = useState([]);
  /* La sotto-schermata puo' arrivare dalla navigazione, come la schermata:
     un avviso che indica una scheda clinica deve aprire QUELLA, non
     l'anamnesi. */
  const sottoProfilo = parametri.get('sotto') || statoNavigazione?.sottoProfilo || 'anamnesi';
  const setSottoProfiloNudo = (s) => scriviIndirizzo({ sotto: s === 'anamnesi' ? null : s });
  const setSottoProfilo = (s) => {
    if (s === sottoProfilo) { setSottoProfiloNudo(s); return; }
    confermaUscitaSalvando().then((si) => { if (si) setSottoProfiloNudo(s); });
  };
  /* E la compilazione precisa dentro quella sotto-schermata. Come
     visitaDaAprire, si consuma una volta sola. */
  const [compilazioneDaAprire] = useState(statoNavigazione?.compilazioneId || null);
  const [showEdit, setShowEdit] = useState(false);   // modale modifica dati
  /* «Indietro» chiude la finestra e resta sulla scheda (28/09/2026). */
  useFinestraIndietro(showEdit, () => { if (!confermaUscita()) return false; setShowEdit(false); return true; });
  // Pazienti importati: 'no' chiuso, 'scelta' dove firmare, 'pc' wizard qui
  const [daVerificare, setDaVerificare] = useState('no');
  // Pannello per registrare o revocare l'opposizione all'AI
  const [opposizione, setOpposizione] = useState(false);
  const [alertData, setAlertData] = useState([]);          // numero dei promemoria
  const [fotoData, setFotoData] = useState([]);            // numero delle foto
  const [showCompletamento, setShowCompletamento] = useState(false); // consensi prelievi

  useEffect(() => { loadPaziente(); /* eslint-disable-next-line */ }, [id]);

  /* Le persone designate per la comunicazione dello stato di salute
     stanno nelle voci del consenso privacy, non in anagrafica: le ha
     scelte il paziente firmando, e la' devono restare. Qui si leggono per
     mostrarle, perche' sono le persone a cui si telefona - averle solo
     dentro un PDF firmato vuol dire non averle. */
  /* Consensi firmati da minorenne senza chi firmava per lui (difetto
     chiuso il 25 settembre 2026): lo decide il server, qui si mostra il
     pulsante. `versioneConsensi` sale dopo una rifirma, cosi' il riquadro
     sparisce senza ricaricare la pagina. */
  const [daRifirmare, setDaRifirmare] = useState(false);
  /* Una pratica in «Accettazioni dal tablet» che la conferma ferma per la
     privacy (revisione del beta, 6/10/2026 sera): il motivo dice di far
     rifirmare i moduli da qui, e qui il pulsante ci dev'essere. Lo decide il
     server (rifirma_privacy), con la stessa regola della coda. */
  const [rifirmaPrivacy, setRifirmaPrivacy] = useState(false);
  const [versioneConsensi, setVersioneConsensi] = useState(0);
  useEffect(() => {
    let vivo = true;
    api.get(`/modulistica/consensi/${id}`)
      .then((r) => {
        if (!vivo) return;
        const elenco = r.data.data.consensi || [];
        const privacy = elenco.find((c) => c.tipo === 'privacy');
        setDesignate(privacy?.voci || null);
        setDaRifirmare(r.data.data.da_rifirmare === true);
        setRifirmaPrivacy(r.data.data.rifirma_privacy === true);
        /* Quanti e su quanti li conta il server (`firmati`, `tipi`): qui si
           scrive il numero e basta. Senza `firmati` niente segno. */
        const tipi = Array.isArray(r.data.data.tipi) ? r.data.data.tipi : null;
        const firmati = Number.isInteger(r.data.data.firmati) ? r.data.data.firmati : null;
        setConsensi(firmati === null ? null : { firmati, su: tipi ? tipi.length : null });
      })
      .catch(() => { /* senza consenso non c'e' niente da mostrare */ });
    return () => { vivo = false; };
  }, [id, versioneConsensi]);

  useEffect(() => {
    let vivo = true;
    polarAPI.mioAccesso()
      .then(r => { if (vivo) setVedeLongevity(r.data.data.longevity === true); })
      .catch(() => { /* nel dubbio non si mostra */ });
    rxAPI.accesso()
      .then(r => { if (vivo) setVedeRx(r.data.data.vede === true); })
      .catch(() => { /* nel dubbio non si mostra */ });

    /* Se la richiesta fallisce si lascia `null`, e le voci restano tutte:
       nel dubbio si mostra. E' il contrario del Registro RX - li' si
       nasconde una cosa che risponderebbe 403, qui si nasconderebbe una
       scheda che il medico ha tutto il diritto di aprire. */
    api.get('/schede/mie-aree')
      .then(r => { if (vivo) setAreeSchede(r.data?.data?.aree || []); })
      .catch(() => { /* restano tutte */ });
    return () => { vivo = false; };
  }, []);

  /* Quali schede del catalogo si compilano nel profilo. Dipende dal
     paziente solo per la strada - l'elenco e' lo stesso per tutti - ma si
     chiede qui perche' la risposta porta anche quante compilazioni ci sono
     gia', e un domani la voce potra' dirlo. */
  useEffect(() => {
    let vivo = true;
    if (!id) return undefined;
    api.get(`/schede/profilo/${id}`)
      .then(r => {
        if (vivo) setSchedeProfilo(r.data?.data?.schede || []);
      })
      .catch(() => { /* nessuna scheda del catalogo: restano le altre voci */ });
    const leggiAvvisi = () => {
      api.get(`/schede/avvisi/${id}`)
        .then(r => { if (vivo) setAvvisiSchede(r.data?.data?.avvisi || []); })
        .catch(() => { /* senza avvisi la scheda si apre lo stesso */ });
    };
    leggiAvvisi();
    /* Si rilegge a ogni scheda salvata, in qualunque punto del gestionale
       sia successo: chi toglie la spunta della controindicazione deve
       vedere sparire la fascia rossa subito, non al prossimo caricamento
       della pagina. */
    window.addEventListener('rimedichub:scheda-salvata', leggiAvvisi);
    return () => {
      vivo = false;
      window.removeEventListener('rimedichub:scheda-salvata', leggiAvvisi);
    };
  }, [id]);

  const loadPaziente = async () => {
    try {
      setLoading(true);
      /* Il fermo di un rilascio di giorno (un secondo e mezzo di 502): la
         lettura riprova da sola una volta (primaLettura.js). */
      const response = await conUnTentativo(() => pazientiAPI.getById(id));
      setLetturaFallita(null);
      if (response.data.success) {
        const pz = response.data.data.paziente;
        const puoCompletare = response.data.data.fa_pratiche === true;
        setPaziente(pz);
        setFaPratiche(puoCompletare);
        /* Paziente accettato solo per prelievi: all'apertura della scheda
           proponi subito il completamento dei consensi mancanti - ma solo a
           chi lo puo' confermare. Fino al 7/10/2026 la finestra si apriva per
           qualunque ruolo: il medico la faceva compilare, il paziente
           firmava, la conferma prendeva 403 e non nasceva nessun documento. */
        if (pz.tipo_accettazione === 'prelievi' && puoCompletare) {
            setShowCompletamento(true);
        }
        try {
          // Unica fonte dell'anamnesi: le voci firmate. I vecchi campi
          // di testo non vanno piu' letti, altrimenti l'avviso allergie
          // mostrerebbe un dato che nessuno aggiorna piu'.
          setAnamnesi(letturaVoci(await anamnesiVociAPI.getByPaziente(id)));
        } catch (e) {
          console.error('Errore caricamento anamnesi', e);
          setAnamnesi({ errore: true });
        }
        try {
          setVisite(letturaVisite(await visiteAPI.getByPazienteId(id)));
        } catch (e) {
          console.error('Errore caricamento procedure', e);
          setVisite({ errore: true });
        }
        try {
          const aRes = await alertAPI.getByPaziente(id);
          if (aRes.data.success) setAlertData(aRes.data.data.alert || []);
        } catch (e) { console.error('Errore caricamento alert', e); }
        try {
          const fRes = await fotoAPI.getByPaziente(id);
          if (fRes.data.success) setFotoData(fRes.data.data.foto || []);
        } catch (e) { console.error('Errore caricamento foto', e); }
        leggiPacchetti();
      }
    } catch (error) {
      console.error('Errore caricamento paziente:', error);
      /* SOLO UN RIFIUTO VERO RIPORTA ALL'ELENCO (Claude, 3/11/2026: «la
         cartella che su un 502 dice "Paziente non trovato" e riporta
         all'elenco [...] manda fuori un medico dalla cartella in mezzo alla
         visita»). Un 404 o un 403 restano come prima. Se la risposta non e'
         arrivata - il riavvio di un rilascio, la rete - la cartella gia'
         aperta resta com'e' con un avviso (questa funzione rilegge anche
         dopo un salvataggio o una firma), e quella che si sta aprendo dice
         il motivo con «Riprova». */
      const esito = esitoLetturaCartella(error, paziente, id);
      if (esito === 'resta') {
        notificaAvviso(motivoDellaLettura(error, 'Non riesco ad aggiornare la cartella'));
      } else if (esito === 'riprova') {
        setPaziente(null);
        setLetturaFallita(motivoDellaLettura(error, 'Non riesco ad aprire la cartella'));
      } else {
        notificaErrore(messaggioDa(error, 'Paziente non trovato'));
        navigate('/pazienti');
      }
    } finally {
      setLoading(false);
    }
  };

  /* I pacchetti si rileggono anche quando cambiano le procedure: una
     seduta eseguita in una visita sposta «2 di 6» in testa. La rotta non
     lascia traccia nel registro degli accessi. Se non risponde, in testa
     non c'e' il segno del pacchetto: e' un'informazione in piu', non un
     avviso. */
  const leggiPacchetti = () => {
    pacchettiAPI.perPaziente(id)
      .then((r) => setPacchettiAperti((r.data?.data?.pacchetti || []).filter((p) => p.aperto === true)))
      .catch(() => setPacchettiAperti([]));
  };

  /* ------------------------------------------------------------------
     La testata si rilegge quando una linguetta cambia i suoi dati.

     Senza questo, la scheda mostrava fino al prossimo F5 quello che
     aveva letto all'apertura: un'allergia appena tolta restava scritta
     nella fascia in testata, un promemoria chiuso risultava aperto, una
     procedura appena registrata non compariva nel riepilogo.

     Sulla fascia delle allergie non e' un conteggio vecchio, e' un
     difetto clinico: mostra un dato che il medico ha appena smentito.
     E' lo stesso che aveva la fascia delle controindicazioni.

     OGNUNO RILEGGE SOLO LA SUA PARTE. Un evento unico «qualcosa e'
     cambiato» costringerebbe a sei richieste ogni volta che si chiude un
     promemoria.

     Gli ascoltatori si staccano all'uscita: senza, ogni riapertura della
     scheda ne aggiunge uno e la stessa lettura parte cinque volte.
     ------------------------------------------------------------------ */
  useEffect(() => {
    let vivo = true;

    /* Una rilettura che non riesce NON lascia quello che c'era: si rilegge
       perche' qualcosa e' appena cambiato, e l'elenco di prima puo' essere
       gia' smentito (un'allergia appena scritta non ci sarebbe, e il segno
       direbbe «nessuna segnalata»). Si dice che non si e' riusciti. */
    const rileggiAnamnesi = () => {
      anamnesiVociAPI.getByPaziente(id)
        .then((r) => { if (vivo) setAnamnesi(letturaVoci(r)); })
        .catch(() => { if (vivo) setAnamnesi({ errore: true }); });
    };

    const rileggiProcedure = () => {
      visiteAPI.getByPazienteId(id)
        .then((r) => { if (vivo) setVisite(letturaVisite(r)); })
        .catch(() => { if (vivo) setVisite({ errore: true }); });
      pacchettiAPI.perPaziente(id)
        .then((r) => { if (vivo) setPacchettiAperti((r.data?.data?.pacchetti || []).filter((p) => p.aperto === true)); })
        .catch(() => {});
    };

    const rileggiAlert = () => {
      alertAPI.getByPaziente(id)
        .then((r) => { if (vivo && r.data.success) setAlertData(r.data.data.alert || []); })
        .catch(() => {});
    };

    const rileggiFoto = () => {
      fotoAPI.getByPaziente(id)
        .then((r) => { if (vivo && r.data.success) setFotoData(r.data.data.foto || []); })
        .catch(() => {});
    };

    const stacca = [
      ascolta('anamnesi', rileggiAnamnesi),
      ascolta('procedure', rileggiProcedure),
      ascolta('alert', rileggiAlert),
      ascolta('foto', rileggiFoto),
    ];

    return () => { vivo = false; stacca.forEach((f) => f()); };
  }, [id]);

  /* L'altezza dell'intestazione ferma, per chi sta sotto e deve fermarsi
     sotto di lei (il pannello della mappa anatomica, MappaAnatomica.css):
     una variabile sul contenitore, misurata e non scritta, perche' i segni
     vanno a capo e l'altezza cambia con loro.
     E LA TESTA FERMA NON PASSA UN TERZO DELLA FINESTRA (revisione del
     6/10/2026): piu' alta, `data-alta` la fa scorrere con la pagina
     (SchedaPaziente.css). L'attributo lo scrive questa misura e non React:
     la posizione si rilegge subito dopo, nello stesso giro.
     L'EFFETTO GUARDA ANCHE `loading` (revisione del 6/10/2026): il paziente
     arriva mentre la pagina dice ancora «Caricamento…», e l'intestazione
     nasce dopo, quando `loading` torna falso. Guardando solo `paziente`
     l'effetto girava quando l'intestazione non c'era ancora e non tornava:
     nessuna misura, ne' la variabile per la mappa ne' il limite d'altezza. */
  const testata = useRef(null);
  const contenitore = useRef(null);
  useEffect(() => {
    const t = testata.current;
    const c = contenitore.current;
    if (!t || !c || typeof ResizeObserver === 'undefined') return undefined;
    const misura = () => {
      const alta = t.getBoundingClientRect().height;
      if (alta > window.innerHeight / 3) t.setAttribute('data-alta', '');
      else t.removeAttribute('data-alta');
      const fissa = getComputedStyle(t).position === 'sticky';
      c.style.setProperty('--crt-sopra', `${fissa ? Math.ceil(alta) : 0}px`);
    };
    misura();
    const o = new ResizeObserver(misura);
    o.observe(t);
    window.addEventListener('resize', misura);
    return () => { o.disconnect(); window.removeEventListener('resize', misura); };
  }, [paziente, loading]);

  /* --- azioni --- */
  const handleEdit = () => setShowEdit(true);          // apre il modale di modifica
  /* Apre la posta della struttura con il destinatario gia' scritto.
     `apriEmail` va chiamata dentro il clic e senza attese in mezzo,
     altrimenti il browser blocca la finestra: il perche' sta in
     utils/posta.js. */
  /* Senza indirizzo (l'email e' facoltativa dal 6/10/2026, punto 29) si
     dice e si propone che cosa fare: le strade che il gestionale ha davvero
     da qui. Il testo e' citato nei manuali e nello scenario 6291. */
  const handleEmail = () => {
    if (!apriEmail(paziente?.email)) {
      notificaAvviso('Nessuna email sulla scheda: aggiungila con "Modifica Dati", '
        + 'oppure scrivi al paziente su WhatsApp o consegnagli i documenti stampati.');
    }
  };
  /* WhatsApp. `apriWhatsApp` torna null se ha aperto, altrimenti il
     motivo: un numero non ricostruibile NON si indovina, perche' una
     chat aperta sul numero sbagliato manda a un estraneo un messaggio
     che nomina un paziente. */
  const handleWhatsApp = () => {
    const problema = apriWhatsApp(paziente?.telefono);
    if (problema) notificaAvviso(problema);
  };

  /* Il telefono e' un segnaposto dichiarato, non un tasto rotto: il
     centralino non e' ancora collegato. Un tasto che non fa niente e non
     lo dice fa pensare a chi lo preme di aver sbagliato lui - e' lo
     stesso difetto che aveva «Invia email» col mailto. */
  const handleTelefono = () => {
    notificaAvviso('La chiamata dalla scheda non e\' ancora attiva: serve il '
        + 'centralino, che non e\' ancora collegato. Il numero del '
        + 'paziente e\' qui in anagrafica.');
  };

  /* «Appuntamento»: il pannello del nuovo appuntamento dell'Agenda, col
     paziente gia' scelto. E' l'azione «Nuovo appuntamento» della barra
     comandi (useAzione), con in piu' il paziente: i campi che il pannello
     mostra, come se l'avesse scelto dalla ricerca (le mancate comprese,
     che il pannello avvisa). Le regole della prenotazione sono quelle della
     rotta. Uscire dalla cartella con un modulo a meta' lo chiede il router
     (GuardiaUscita), come per una voce del menu. */
  const handleAppuntamento = () => {
    navigate('/agenda', { state: { azione: 'nuovo-appuntamento', paziente: {
      id: paziente.id, cognome: paziente.cognome, nome: paziente.nome, id_paziente: paziente.id_paziente,
      data_nascita: paziente.data_nascita, telefono: paziente.telefono,
      mancate_12m: paziente.mancate_12m, ultima_mancata: paziente.ultima_mancata,
    } } });
  };

  const calcolaEta = (d) => {
    if (!d) return null;
    const n = new Date(d); if (isNaN(n)) return null;
    const oggi = new Date();
    let eta = oggi.getFullYear() - n.getFullYear();
    const m = oggi.getMonth() - n.getMonth();
    if (m < 0 || (m === 0 && oggi.getDate() < n.getDate())) eta--;
    return eta;
  };

  if (loading) return <div className="sp-loading">Caricamento…</div>;
  if (!paziente && letturaFallita) {
    return (
      <div className="sp-loading">
        {letturaFallita}{' '}
        <button type="button" className="sp-riprova" onClick={loadPaziente}>Riprova</button>
      </div>
    );
  }
  if (!paziente) return <div className="sp-loading">Paziente non trovato</div>;

  /* Una domanda sola anche quando cambiano linguetta e sotto-linguetta
     insieme: due setter protetti in fila chiederebbero due volte.
     Prima si salva (confermaUscitaSalvando, come setSchermata): la risposta
     puo' arrivare dopo, quindi quello che segue il cambio di linguetta si
     passa in `poi`, che parte solo se la linguetta e' cambiata davvero. */
  const vaiA = (target, sotto = target === 'profilo' ? 'anamnesi' : null, poi = null) => {
    const cambia = target !== schermata || (sotto && sotto !== sottoProfilo);
    const vai = () => {
      /* Un passo solo nella cronologia, linguetta e sotto-linguetta insieme. */
      scriviIndirizzo({ vista: target === 'home' ? null : target,
                        sotto: target === 'profilo' ? (sotto && sotto !== 'anamnesi' ? sotto : null) : null,
                        visita: null });
      if (poi) poi();
    };
    if (!cambia) { vai(); return; }
    confermaUscitaSalvando().then((si) => { if (si) vai(); });
  };

  const handleNuovaVisita = () => {
    vaiA('procedure', undefined, () => setNuovaVisitaRichiesta((n) => n + 1));
  };

  /* LE LINGUETTE, coi numeri delle cose che la cartella ha gia' letto
     aprendosi (visite, foto, promemoria aperti): nessuna lettura in piu'
     per un numero. Lo zero non si scrive.

     `titolo` e' il nome intero, quello di prima: resta nel `title` della
     linguetta, e le prove cercano la linguetta da li'. `principale` e
     `tinta` non ci sono piu': la linguetta accesa e' l'unica cosa che si
     distingue, come nel menu.

     Gli incassi stanno solo ad amministrazione e segreteria. «Cassa» e' il
     nome dei manuali (Alex, 28/09/2026): la linguetta si chiamava «Calcolo
     incassi e fatturazione», e da amministratore non l'ha trovata.

     I referti SCRITTI qui stanno accanto ai Documenti e non dentro:
     quelli sotto Documenti sono i file RICEVUTI da fuori, e mescolarli
     farebbe sembrare che un referto della clinica e uno di un
     laboratorio siano la stessa cosa. Di uno rispondiamo noi. */
  /* Un numero che non si e' riuscito a leggere non si scrive, come lo zero:
     la stessa regola dei numeri del menu (useContatori). La linguetta non
     afferma niente; il riepilogo, che afferma, dice che non ha letto. */
  const promemoriaAperti = (alertData || []).filter((a) => a.stato !== 'fatto').length;
  /* `breve` e' il nome nella riga del telefono, che non scorre di lato
     (LinguetteCartella, proposta telefono-stile-nuovo punto 3): sul
     computer resta `label`. */
  const LINGUETTE = [
    { id: 'home',      label: 'Riepilogo',          breve: 'Riepilogo', titolo: 'Riepilogo' },
    { id: 'profilo',   label: 'Profilo medico',     breve: 'Profilo',   titolo: 'Profilo Medico' },
    { id: 'procedure', label: 'Visite e procedure', breve: 'Visite',    titolo: 'Visite e procedure mediche', conta: visite?.elenco?.length },
    { id: 'documenti', label: 'Documenti',          breve: 'Documenti', titolo: 'Documenti, ricette e consensi' },
    { id: 'referti',   label: 'Referti scritti qui', breve: 'Referti',  titolo: 'Referti scritti qui' },
    { id: 'foto',      label: 'Foto',               breve: 'Foto',      titolo: 'Foto', conta: fotoData.length },
    ...(vedeIncassi ? [{ id: 'incassi', label: 'Cassa', breve: 'Cassa', titolo: 'Cassa' }] : []),
    { id: 'alert',     label: 'Promemoria',         breve: 'Promemoria', titolo: 'Promemoria', conta: promemoriaAperti },
    ...(vedeLongevity ? [{ id: 'longevity', label: 'Longevity', breve: 'Longevity', titolo: 'Longevity' }] : []),
    ...(vedeRx ? [{ id: 'rx', label: 'Registro RX', breve: 'RX', titolo: 'Registro RX' }] : []),
  ];

  const eta = calcolaEta(paziente.data_nascita);
  /* Il sesso come lo dice la scheda, e niente se la scheda non lo dice
     (revisione del 6/10/2026: con il sesso vuoto la testa scriveva «F»
     accanto a «NATO», e i dati del paziente «Femmina»). */
  const sesso = paziente.sesso === 'M' || paziente.sesso === 'F' ? paziente.sesso : null;
  const nato = sesso === 'F' ? 'NATA' : (sesso === 'M' ? 'NATO' : 'NATO/A');
  const nascita = dataPunti(paziente.data_nascita);
  /* Le allergie attive del segno in testa: `null` se l'anamnesi non e'
     stata letta (in attesa o non riuscita), un elenco - anche vuoto - se si'. */
  const avvisiTestata = anamnesi?.elenco ? avvisiDellaTestata(anamnesi.dati) : null;
  const vociAllergie = avvisiTestata ? avvisiTestata.allergie : null;
  const telefono = String(paziente.telefono || '').trim();
  const pacchettoInTesta = pacchettiAperti[0];

  /* Le sotto-schermate servite da una scheda a campi: la voce di menu sta
     a sinistra, l'area del questionario a destra. Quella che resta fuori
     - Foto - non e' una scheda a campi. */
  const AREE_SCHEDE = {
    pelle: 'pelle',
    tricologico: 'tricologia',
    composizione: 'composizione',
    misure: 'misure',
  };

  /* IL PROFILO E' DIVISO IN AREE, NON E' UNA FILA DI ICONE.
     Nove icone in fila si distinguono solo per il disegno, e per sapere
     dove si va bisogna passarci sopra una a una. Raggruppate per area, con
     il titolo sempre scritto sopra, il colpo d'occhio basta: chi cerca la
     tricologia guarda PELLE e non le altre otto.

     FOTO NON C'E' PIU' QUI, e non e' sparita: sta nelle linguette del
     paziente, che e' il posto giusto - le fotografie sono del paziente,
     non di un'area clinica. Averla in due posti voleva solo dire due strade
     per la stessa cosa.

     Un'area senza nessuna voce visibile non si disegna: un riquadro col
     titolo e dentro il vuoto e' peggio del riquadro che non c'e'. */
  /* LE SCHEDE DEL CATALOGO SI DERIVANO, NON SI ELENCANO A MANO.
     Fino al 22 settembre 2026 ogni scheda di schede_struttura era una
     riga scritta qui: la cardiologia e' stata attiva e invisibile per
     tre ore perche' nessuno aveva aggiunto la riga. Adesso
     /api/schede/profilo/:id dice quali schede sono attive, con area,
     nome e ordine, e le voci nascono da li'. Quello che resta scritto
     a mano e' solo COME disegnarle: il titolo del gruppo per un'area
     (per unire ortopedia, fisiatria e fisioterapia, e per mettere la
     medicina estetica sotto Pelle) e l'icona; un'area nuova senza voce
     qui esce col suo nome e un'icona generica - visibile, non sparita. */
  const AREE_PROFILO = areeProfilo(schedeProfilo);

  const SUB_PROFILO = AREE_PROFILO.flatMap((a) => a.voci);

  /* Le voci che cominciano per `scheda_` sono schede del catalogo dei
     campi, e compaiono solo se quella scheda esiste, e' attiva e ha dei
     campi dentro: l'elenco arriva dal server (`schedeProfilo`), cosi' una
     scheda nuova appare senza toccare questa schermata. */
  /* ATTENZIONE: non c'e' piu' un ramo di ripiego in fondo alla catena qui
     sotto. Ogni id elencato qui DEVE avere il suo ramo, altrimenti l'area
     resta vuota senza dire niente. Prima il ripiego c'era ed era il
     riquadro «In arrivo», ma lo raggiungeva una voce sola. */
  /* Le voci servite da una scheda clinica compaiono solo se quella scheda
     e' fra quelle di questo medico. Le altre - anamnesi, foto, esami,
     profilo alimentare - non sono schede della struttura e restano
     sempre. */
  const AREA_DI = { ...AREE_SCHEDE, gastro: 'gastroenterologia' };
  const subVisibili = SUB_PROFILO.filter((v) => {
    if (v.id.startsWith('scheda_')) return true;   // derivate: ci sono solo se attive
    const area = AREA_DI[v.id];
    if (!area || areeSchede === null) return true;
    return areeSchede.includes(area);
  });
  const subLabel = SUB_PROFILO.find(s => s.id === sottoProfilo)?.label;

  /* I DATI DEL PAZIENTE, in fondo al riepilogo su tutta la larghezza:
     l'anagrafica a sinistra, le persone e le note a destra. Sono quelli di
     prima, con le stesse regole (le persone di riferimento dal consenso, il
     curante, le note che si salvano da sole). */
  const datiPaziente = (
    <div className="sp-card">
      <div className="sp-card-head">Dati del paziente</div>
      <div className="sp-card-body crt-dati-corpo">
        <div className="crt-dati-colonna">
          <Riga label="Codice paziente" value={paziente.id_paziente} />
          <Riga label="Codice fiscale" value={paziente.codice_fiscale} />
          <Riga label="Data di nascita"
                value={paziente.data_nascita ? new Date(paziente.data_nascita).toLocaleDateString('it-IT') : 'N/A'} />
          <Riga label="Luogo di nascita" value={paziente.luogo_nascita} />
          <Riga label="Sesso" value={sesso === 'M' ? 'Maschio' : (sesso === 'F' ? 'Femmina' : null)} />
          <div className="sp-sep" />
          <Riga label="Telefono" value={paziente.telefono} />
          <Riga label="Email" value={paziente.email} />
          <div className="sp-sep" />
          <Riga label="Indirizzo" value={paziente.indirizzo} />
          <Riga label="Città" value={paziente.citta} />
          <Riga label="CAP" value={paziente.cap} />
          <Riga label="Provincia" value={paziente.provincia} />
        </div>
        <div className="crt-dati-colonna">
          {/* Chi chiamare, secondo il consenso firmato. Non si
              modifica da qui: e' una scelta che il paziente ha
              firmato, e cambiarla in anagrafica renderebbe il PDF
              archiviato diverso da cio' che il gestionale mostra.
              Per cambiarla si rifa' il consenso. */}
          <div className="sp-row sp-row-blocco">
            <span className="sp-row-label">Persone di riferimento</span>
            <div className="sp-row-valore">
              {designate?.familiari !== true ? (
                <span className="sp-vuoto-nota">
                  {designate
                    ? 'Il paziente non ha autorizzato la comunicazione a terzi'
                    : 'Consenso privacy non ancora firmato'}
                </span>
              ) : (
                <>
                  {[1, 2].map((n) => {
                    const nome = n === 1 ? designate.familiare_nome : designate.familiare2_nome;
                    if (!nome) return null;
                    const rel = n === 1 ? designate.familiare_relazione : designate.familiare2_relazione;
                    const rec = n === 1 ? designate.familiare_recapito : designate.familiare2_recapito;
                    return (
                      <div key={n} className="sp-designata">
                        <b>{nome}</b>
                        {rel && <span className="sp-designata-rel">{rel}</span>}
                        {rec && <span className="sp-designata-rec">{rec}</span>}
                      </div>
                    );
                  })}
                  {!designate.familiare_nome && (
                    <span className="sp-vuoto-nota">Autorizzata, ma nessun nome indicato</span>
                  )}
                  <span className="sp-vuoto-nota">
                    dal consenso privacy: si cambia rifacendo il consenso
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Il medico curante: campo suo dal 4 settembre 2026, perche'
              e' UNO, lo dichiara il paziente in accettazione e serve
              poterlo leggere da qui senza cercarlo in mezzo a un testo
              libero. La spunta «non ne ha» distingue il paziente senza
              curante da quello a cui nessuno l'ha chiesto. */}
          <div className="sp-row">
            <span className="sp-row-label">Medico curante</span>
            <span className="sp-row-value">
              {paziente.medico_curante_assente
                ? <em className="sp-assente">il paziente ha dichiarato di non averne</em>
                : paziente.medico_curante
                  ? <>
                      {paziente.medico_curante}
                      {paziente.medico_curante_contatto
                        && <span className="sp-curante-contatto">
                             {paziente.medico_curante_contatto}
                           </span>}
                    </>
                  : <em className="sp-assente">non richiesto</em>}
            </span>
          </div>

          {/* Gli altri professionisti esterni. Si chiamava «Medici di
              riferimento» e conteneva anche il curante: ora sono due
              campi, e questo resta il testo libero per il resto. */}
          <div className="sp-row sp-row-note-edit">
            <span className="sp-row-label">Altri professionisti</span>
            <div className="sp-row-note-campo">
              <NotaInline
                pazienteId={paziente.id}
                valoreIniziale={paziente.altri_professionisti}
                salva={pazientiAPI.updateAltriProfessionisti}
                placeholder="Specialisti, fisioterapisti, altri professionisti…"
                righe={2}
                onSaved={(nuovo) => setPaziente((p) => ({ ...p, altri_professionisti: nuovo }))}
              />
            </div>
          </div>

          {/* Medicina del lavoro (proposta 18): le aziende del lavoratore, con le date. */}
          <RiquadroLavoro pazienteId={paziente.id} ruolo={ruolo} />

          {/* L'opposizione all'elaborazione con strumenti di intelligenza
              artificiale. Stava fra le icone in testa («Elaborazione
              automatizzata»); adesso sta coi dati del paziente, con lo
              stato scritto accanto e lo stesso pannello per cambiarlo. */}
          <div className="sp-row">
            <span className="sp-row-label">Intelligenza artificiale</span>
            <span className="sp-row-value">
              {paziente.opposizione_ai
                ? <>si è opposto{paziente.opposizione_ai_il
                    && ` dal ${new Date(paziente.opposizione_ai_il).toLocaleDateString('it-IT')}`}</>
                : <em className="sp-assente">nessuna opposizione</em>}
              {/* Apre il pannello di prima, che spiega e chiede conferma. */}
              <button type="button" className="crt-link crt-link--riga" onClick={() => setOpposizione(true)}>
                {paziente.opposizione_ai ? 'Revoca l\'opposizione…' : 'Registra l\'opposizione…'}
              </button>
            </span>
          </div>

          <div className="sp-row sp-row-note-edit">
            <span className="sp-row-label">Note</span>
            <div className="sp-row-note-campo">
              <NotaInline
                pazienteId={paziente.id}
                valoreIniziale={paziente.note}
                onSaved={(nuovo) => setPaziente((p) => ({ ...p, note: nuovo }))}
              />
            </div>
          </div>
        </div>
      </div>
      {scriveAnagrafica && <button className="sp-btn-outline" onClick={handleEdit}>Modifica dati</button>}
    </div>
  );

  return (
    <div className="sp-container" ref={contenitore}>

      {/* ---------- INTESTAZIONE ---------- */}
      <header className="sp-header crt-testata" ref={testata}>
        <div className="crt-testata-riga">
          <div className="crt-iniziali" aria-hidden="true">{iniziali(paziente)}</div>
          <div className="crt-identita">
            <span className="sp-pid rm-numeri">{paziente.id_paziente}</span>
            <div className="crt-nome-riga">
              <h1 className="sp-name">{paziente.nome} {paziente.cognome}</h1>
              {paziente.tipo_accettazione === 'prelievi' && (
                <span className="rm-stato rm-stato-attenzione" title="Consensi non completi">Prelievi</span>
              )}
              {paziente.longevity === true && (
                <span className="rm-stato rm-stato-accento">Longevity</span>
              )}
              {paziente.opposizione_ai === true && (
                <span className="rm-stato rm-stato-neutro"
                      title="Il paziente ha chiesto che i suoi dati non siano elaborati con strumenti di intelligenza artificiale">
                  No AI
                </span>
              )}
              {/* Dopo il nome e non fra le etichette corte: porta la data, ed e'
                  lungo (F2, Alex 4/10/2026). Il server lo calcola con la regola
                  dell'invio al Sistema TS. */}
              <SegnoOpposizioneTs attiva={paziente.opposizione_ts_attiva} dal={paziente.opposizione_ts_dal} />
            </div>
            <div className="crt-meta rm-numeri">
              {(sesso || eta != null) && (
                <span>{[sesso, eta != null ? `${eta} ANNI` : null].filter(Boolean).join(' · ')}</span>
              )}
              {nascita && <span>{nato} {nascita}</span>}
              {telefono && <span className="sp-tel">TEL {telefono}</span>}
            </div>
          </div>
          <div className="crt-azioni">
            <div className="crt-contatti">
              {/* I tre modi di raggiungere il paziente, come prima: i nomi
                  interi stanno nel `title`. Il telefono e' un segnaposto
                  dichiarato (tratteggiato), e premuto dice perche'. */}
              <button type="button" className="crt-icona crt-icona--whatsapp" onClick={handleWhatsApp}
                      title="Scrivi su WhatsApp" aria-label="Scrivi su WhatsApp">
                <IconaWhatsApp size={17} />
              </button>
              <button type="button" className="crt-icona crt-icona--spento" onClick={handleTelefono}
                      title="Chiama (non ancora attivo)" aria-label="Chiama (non ancora attivo)">
                <Phone size={16} strokeWidth={1.8} />
              </button>
              <button type="button" className="crt-icona" onClick={handleEmail}
                      title="Invia Email" aria-label="Invia Email">
                <Mail size={16} strokeWidth={1.8} />
              </button>
            </div>
            {/* «Modifica dati» anche in testa (C4, Alex 13/10/2026): prima stava solo in
                fondo, sotto i dati. Lo vede chi puo' scrivere l'anagrafica, come
                il pulsante in fondo: il server rifiuterebbe gli altri con un 403. */}
            {scriveAnagrafica && (
              <button type="button" className="crt-btn" data-azione="modifica-dati" onClick={handleEdit}>
                Modifica dati
              </button>
            )}
            <button type="button" className="crt-btn" onClick={handleAppuntamento}>Appuntamento</button>
            {registraVisite && (
              <button type="button" className="crt-btn crt-btn--primario" onClick={handleNuovaVisita}
                      title="La visita di oggi, vuota: si compila «Visita», poi le prestazioni da «Nuova procedura».">
                Nuova visita
              </button>
            )}
          </div>
        </div>

        {/* I SEGNI: quello che va saputo prima di toccare il paziente. */}
        <div className="crt-segni">
          {consensi && (
            <button type="button" data-segno="consensi"
                    className={`crt-segno ${consensi.su !== null && consensi.firmati >= consensi.su ? 'crt-segno--ok' : 'crt-segno--attenzione'}`}
                    onClick={() => vaiA('documenti')}
                    title="I consensi firmati dal paziente: privacy, fotografie, firma elettronica. Si vedono fra i documenti.">
              <span className="crt-pallino" aria-hidden="true" />
              Consensi {consensi.su !== null ? `${consensi.firmati}/${consensi.su}` : consensi.firmati} firmati
            </button>
          )}
          {pacchettoInTesta && (() => {
            const intero = `${pacchettoInTesta.descrizione} · ${Number(pacchettoInTesta.sedute_eseguite) || 0} di ${Number(pacchettoInTesta.numero_sedute) || 0} eseguite${pacchettiAperti.length > 1 ? ` · altri ${pacchettiAperti.length - 1}` : ''}`;
            return (
            <span className="crt-segno crt-segno--stretto" data-segno="pacchetto" title={intero}>
              {/* Una riga sola, coi puntini, intero nel title (3/11/2026: la
                  testa ferma a 1024x768, vedi SegniAvvisi). Il title anche
                  sullo span che si tronca: e' li' che lo cerca chi legge, e la
                  rifinitura (NO del beta del 3/11 sera, 72 difetti). */}
              <span className="crt-segno-testo rm-tronca" title={intero}>
                {pacchettoInTesta.descrizione} · {Number(pacchettoInTesta.sedute_eseguite) || 0} di {Number(pacchettoInTesta.numero_sedute) || 0} eseguite
                {pacchettiAperti.length > 1 && ` · altri ${pacchettiAperti.length - 1}`}
              </span>
            </span>
            );
          })()}
          {/* LE ALLERGIE: un segno solo, che porta all'anamnesi. Rosso se ce
              ne sono, con la gravita' accanto: e' l'informazione piu'
              importante della scheda, e da qui resta in vista mentre si
              lavora. Fino al 6/10 era una fascia sotto la testata, che
              scorreva via con la pagina.
              TRE STATI, non due (revisione del 6/10/2026): «nessuna
              segnalata» SOLO se l'anamnesi e' stata letta e non ne ha; se la
              lettura non e' riuscita il segno lo dice, in giallo, e porta
              all'anamnesi, che le rilegge per conto suo. Finche' la lettura
              non arriva il segno non c'e'. */}
          {anamnesi?.errore && (
            <button type="button" data-segno="allergie" data-lettura="non-riuscita"
                    className="crt-segno crt-segno--attenzione"
                    onClick={() => vaiA('profilo')}>
              <TriangleAlert size={14} strokeWidth={2} aria-hidden="true" />
              Allergie: non riesco a leggerle, apri l'anamnesi
            </button>
          )}
          {vociAllergie && (
            <button type="button" data-segno="allergie"
                    className={`crt-segno crt-segno--allergie ${vociAllergie.length ? 'crt-segno--errore' : 'crt-segno--neutro'}`}
                    onClick={() => vaiA('profilo')}>
              {vociAllergie.length > 0 && <TriangleAlert size={14} strokeWidth={2} aria-hidden="true" />}
              {vociAllergie.length === 0 ? 'Allergie: nessuna segnalata' : (
                <span>
                  Allergie:{' '}
                  {vociAllergie.map((v, i) => (
                    <span key={v.id || i} className="sp-allergia">
                      {i > 0 && ' · '}{v.testo}
                      {v.gravita && <em className={`sp-allergia-gravita gravita-${v.gravita}`}>{v.gravita}</em>}
                    </span>
                  ))}
                </span>
              )}
            </button>
          )}
          {/* PATOLOGIE CUTANEE ED HERPES (Alex, 3/11/2026): col «sì» un segno
              accanto alle allergie, rosso come loro, col testo se c'e'; col
              «no» o senza risposta niente. Li decide avvisiDellaTestata. Se
              il server non e' riuscito a leggerli, lo dice in giallo. */}
          {/* E GLI AVVISI DELLE SCHEDE CLINICHE: stessa forma delle allergie, e
              non e' pigrizia - per chi legge sono la stessa cosa. Non portano
              da nessuna parte: verrebbero da una compilazione fra tante.
              Una riga ciascuno, coi puntini e il testo intero nel title, e
              oltre i primi due un «+N» che apre l'elenco (NO del beta del
              3/11/2026: la testa a 1024x768 passava il terzo della finestra
              e smetteva di restare ferma). Le regole stanno in SegniAvvisi. */}
          <SegniAvvisi risposte={avvisiTestata?.risposte || []}
                       risposteNonLette={Boolean(avvisiTestata?.risposteNonLette)}
                       avvisiSchede={avvisiSchede}
                       onApri={() => vaiA('profilo')} />
        </div>

        {/* ---------- LINGUETTE ---------- */}
        {/* Sul computer la riga di prima; sul telefono del medico le prime
            quattro e «Altro», in una riga che non scorre (punto 3 della
            proposta telefono-stile-nuovo): lo stesso criterio di «Carica». */}
        <LinguetteCartella linguette={LINGUETTE} attiva={schermata}
                           onScegli={setSchermata} telefono={caricaDalTelefono} />
      </header>

      {/* ---------- PAZIENTE IMPORTATO ----------
          Solo a chi puo' completare la pratica (faPratiche, dal server),
          come i due riquadri della rifirma qui sotto. Fino al 7/10/2026 il
          riquadro e il pulsante si vedevano anche dal medico: «Su questo
          computer» (o nessun tablet, e SceltaFirma ci va da sola) apriva la
          pratica, il paziente firmava quattro moduli e la conferma prendeva
          403 (revisione del beta su ce78ace). */}
      {paziente.anagrafica_verificata === false && faPratiche && (
        <div className="sp-verifica-banner">
          <div>
            <b>Anagrafica da completare</b>
            <p>
              Questo paziente proviene dal vecchio archivio: i suoi recapiti
              potrebbero mancare e i consensi sono ancora quelli cartacei.
              Fai controllare i dati e firmare i moduli nuovi.
            </p>
          </div>
          <button onClick={() => setDaVerificare('scelta')}>
            Completa la pratica
          </button>
        </div>
      )}

      {/* ---------- MODULI DA RIFIRMARE ----------
          Consensi firmati quando il paziente era minorenne, senza il
          genitore o il tutore accanto alla firma. Il pulsante apre la
          stessa pratica di «Completa la pratica»: quattro moduli nuovi,
          con chi firma per il minore; i vecchi restano nello storico. */}
      {daRifirmare && faPratiche && (
        <div className="sp-verifica-banner">
          <div>
            <b>Moduli da far rifirmare</b>
            <p>
              I consensi di questo paziente sono stati firmati quando era
              minorenne, ma senza il nome di chi firmava per lui: sulla
              carta firma il minore. Falli rifirmare a un genitore o al
              tutore (se nel frattempo è maggiorenne, firma lui).
            </p>
          </div>
          <button onClick={() => setDaVerificare('scelta')}>
            Fai rifirmare i moduli
          </button>
        </div>
      )}

      {/* ---------- PRATICA DAL TABLET DA FAR RIFIRMARE ----------
          La stessa pratica di «Fai rifirmare i moduli» qui sopra, con lo
          stesso pulsante e gli stessi ruoli. Sparisce da sola a rifirma
          fatta (versioneConsensi): il server non conta piu' la pratica
          superata da un modulo della privacy firmato dopo. */}
      {rifirmaPrivacy && !daRifirmare && faPratiche && (
        <div className="sp-verifica-banner" data-rifirma="privacy">
          <div>
            <b>Pratica dal tablet da far rifirmare</b>
            <p>
              In «Accettazioni dal tablet» c'è una pratica di questo paziente
              compilata al tablet come paziente nuovo, che non si può
              archiviare: nel modulo della privacy la voce sul Sistema TS dice
              il contrario di questa scheda, e il paziente non l'ha toccata.
              Fai rifirmare i moduli: partono da quello che dice la scheda, e
              la scelta è del paziente. Poi scarta la pratica rimasta in coda.
            </p>
          </div>
          <button onClick={() => setDaVerificare('scelta')}>
            Fai rifirmare i moduli
          </button>
        </div>
      )}

      {/* ---------- BANNER CONSENSI DA COMPLETARE ---------- */}
      {/* Il completamento lo fanno segreteria e amministrazione: sono i ruoli
          della rotta (revisione del beta, 6/10/2026 sera). L'avviso resta per
          tutti, il gesto no. */}
      {paziente.tipo_accettazione === 'prelievi' && (
        <div className="sp-banner-prelievi" onClick={faPratiche ? () => setShowCompletamento(true) : undefined}>
          <span className="sp-banner-goccia"><Droplet size={16} strokeWidth={1.8} aria-hidden="true" /></span>
          <span>Paziente accettato solo per il punto prelievi — consensi da completare.</span>
          {faPratiche && (
            <button className="sp-banner-btn" onClick={(e) => { e.stopPropagation(); setShowCompletamento(true); }}>
              Completa consensi
            </button>
          )}
        </div>
      )}

      {/* ---------- CORPO ---------- */}
      <div className="sp-body">

        {schermata === 'home' && (
          <RiepilogoCartella
            pazienteId={paziente.id}
            visite={visite}
            anamnesi={anamnesi}
            pacchetti={pacchettiAperti}
            onApriVisita={(vid) => vaiA('procedure', undefined, () => setVisitaDaAprire(vid))}
            onTutteLeVisite={() => vaiA('procedure')}
            onAggiungiTerapia={() => vaiA('profilo', 'anamnesi', () => setTerapiaDaAggiungere(true))}
            onApriGiorno={(giorno) => navigate(`/agenda?giorno=${giorno}`)}
          >
            {datiPaziente}
          </RiepilogoCartella>
        )}

        {schermata === 'profilo' && (
          <div className="sp-screen">
            <div className="sp-subtitle">Profilo medico del paziente</div>

            <div className="sp-aree">
              {AREE_PROFILO.map((area) => {
                const voci = area.voci.filter((v) => subVisibili.some((x) => x.id === v.id));
                if (!voci.length) return null;
                return (
                  <div className="sp-area" key={area.titolo}>
                    <div className="sp-area-titolo">{area.titolo}</div>
                    <div className="sp-area-icone">
                      {voci.map((s) => {
                        const IconComp = s.icon;
                        const attivo = sottoProfilo === s.id;
                        return (
                          <button key={s.id}
                            className={`sp-subbtn ${attivo ? 'attivo' : ''}`}
                            onClick={() => setSottoProfilo(s.id)}
                            title={s.label}>
                            <IconComp />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Qui stava «+ Nuovo Referto», che apriva un alert con scritto
                «in arrivo nel prossimo blocco». Compariva su Foto, Esami e
                Referti e Diario clinico, cioe' per esclusione su tutto quello
                che non e' una scheda a campi.

                Tolto il 12 settembre 2026. Un pulsante che non fa niente non e'
                neutro: chi lo preme la prima volta perde tempo, chi lo preme la
                seconda smette di fidarsi anche dei pulsanti che funzionano.
                Lo stesso giorno, e per la stessa ragione, e' sparita la voce
                «Diario Clinico / Note»: le note sul paziente stanno gia' nella
                scheda anagrafica.

                Quando il referto si potra' davvero creare, il pulsante torna —
                e allora fara' qualcosa. */}
            <div className="sp-sublabel">
              <span>{subLabel}</span>
            </div>

            {sottoProfilo.startsWith('scheda_')
              /* «apri l'anamnesi» dal riquadro delle gravidanze di un giorno
                 passato: la stessa strada della linguetta, con la stessa
                 domanda se c'e' qualcosa da salvare. */
              ? <SchedeProfilo pazienteId={id} schedaCodice={sottoProfilo}
                               onApriAnamnesi={() => setSottoProfilo('anamnesi')} />
              : sottoProfilo === 'anamnesi'
              ? <AnamnesiTab pazienteId={id} sessoPaziente={paziente.sesso}
                             onApriVisita={(vid) => vaiA('procedure', undefined, () => setVisitaDaAprire(vid))}
                             aggiungiTerapia={terapiaDaAggiungere}
                             onTerapiaAperta={() => setTerapiaDaAggiungere(false)} />
              : sottoProfilo === 'sangue'
              ? <EsamiRefertiTab pazienteId={id} />
              : sottoProfilo === 'alimentare'
              ? <ProfiloAlimentareTab pazienteId={id}
                                      apriCompilazione={compilazioneDaAprire} />
              : sottoProfilo === 'gastro'
              ? <>
                  {/* Fumo e alcol dall'anamnesi, in sola lettura (Alex,
                      27/09/2026): il questionario ha ancora le sue domande,
                      che toglieranno i medici validando (docs/campi). */}
                  <RiquadroAbitudini pazienteId={id} valoreDiOggi
                                     onApriAnamnesi={() => setSottoProfilo('anamnesi')} />
                  <ElencoQuestionari pazienteId={id} area="gastroenterologia" etichetta="scheda"
                                     apriCompilazione={compilazioneDaAprire} />
                </>
              /* Le quattro schede cliniche stanno sulla stessa struttura
                 della gastroenterologica: sezioni, campi tipizzati,
                 intervalli. Una seconda impalcatura per ognuna avrebbe
                 voluto dire quattro schermate da tenere allineate a mano. */
              : AREE_SCHEDE[sottoProfilo]
              ? <>
                  {/* L'andamento del peso sta SOPRA l'elenco delle
                      compilazioni, e solo sulle due schede nutrizionali:
                      e' la domanda che ci si fa aprendo la scheda -
                      «come sta andando» - e l'elenco delle date da sola
                      non la risponde. Il riquadro non compare finche'
                      non c'e' almeno una misura. */}
                  {(sottoProfilo === 'misure' || sottoProfilo === 'composizione') && (
                    <AndamentoPeso pazienteId={id} />
                  )}
                  {/* Fumo e sigarette dall'anamnesi, in sola lettura, come
                      sopra la scheda gastroenterologica. */}
                  {sottoProfilo === 'pelle' && (
                    <RiquadroAbitudini pazienteId={id} valoreDiOggi
                                       onApriAnamnesi={() => setSottoProfilo('anamnesi')} />
                  )}
                  <ElencoQuestionari pazienteId={id}
                                     area={AREE_SCHEDE[sottoProfilo]}
                                     etichetta="scheda"
                                     apriCompilazione={compilazioneDaAprire} />
                </>
              : null}
          </div>
        )}

        {schermata === 'documenti'  && <div className="sp-screen"><DocumentiTab pazienteId={id} /></div>}
        {schermata === 'referti'    && <div className="sp-screen"><RefertiScritti pazienteId={id} /></div>}
        {schermata === 'alert'      && <div className="sp-screen"><AlertTab pazienteId={id} /></div>}
        {schermata === 'procedure'  && (
          <div className="sp-screen">
            <ProcedureMediche pazienteId={id} sessoPaziente={paziente.sesso}
                              visitaDaAprire={visitaDaAprire} onVisitaAperta={() => setVisitaDaAprire(null)}
                              nuovaVisitaRichiesta={nuovaVisitaRichiesta}
                              onNuovaVisitaPresa={() => setNuovaVisitaRichiesta(0)} />
          </div>
        )}
        {schermata === 'foto'       && <div className="sp-screen"><FotoTab pazienteId={id} /></div>}
        {schermata === 'incassi' && vedeIncassi && <div className="sp-screen"><FatturazioneTab pazienteId={id} /></div>}
        {schermata === 'longevity' && vedeLongevity && <div className="sp-screen"><LongevityTab pazienteId={id} /></div>}
        {schermata === 'rx'        && vedeRx        && <div className="sp-screen"><RegistroRx pazienteId={id} /></div>}

      </div>

      {/* ---------- MODALE MODIFICA DATI ---------- */}
      {showEdit && scriveAnagrafica && (
        <PazienteForm
          paziente={paziente}
          onClose={() => setShowEdit(false)}
          onSuccess={loadPaziente}
        />
      )}

      {showCompletamento && faPratiche && paziente.tipo_accettazione === 'prelievi' && (
        <CompletamentoConsensi
          paziente={paziente}
          onClose={() => setShowCompletamento(false)}
          onSuccess={() => { setShowCompletamento(false); loadPaziente(); }}
        />
      )}
      {/* ---------- OPPOSIZIONE ALL'ELABORAZIONE AUTOMATIZZATA ---------- */}
      {opposizione && (
        <div className="sp-opp-sfondo" onClick={() => setOpposizione(false)}>
          <div className="sp-opp" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h3>Elaborazione con strumenti di intelligenza artificiale</h3>

            {paziente.opposizione_ai ? (
              <>
                <p>
                  Questo paziente ha chiesto che i suoi dati non siano elaborati
                  con strumenti di intelligenza artificiale
                  {paziente.opposizione_ai_il &&
                    ` dal ${new Date(paziente.opposizione_ai_il).toLocaleDateString('it-IT')}`}.
                  Le relative attività vanno svolte manualmente.
                </p>
                <div className="sp-opp-azioni">
                  <button onClick={() => setOpposizione(false)}>Chiudi</button>
                  <button className="sp-opp-revoca" onClick={async () => {
                    try {
                      await api.post(`/ai/opposizione/${paziente.id}`, { opposto: false });
                      setOpposizione(false);
                      loadPaziente();
                    } catch (e) {
                      notificaErrore(messaggioDa(e, 'Non riesco a revocare l\'opposizione'));
                    }
                  }}>
                    Revoca l'opposizione
                  </button>
                </div>
              </>
            ) : (
              <>
                <p>
                  Registra la richiesta del paziente di non essere sottoposto a
                  elaborazione mediante strumenti di intelligenza artificiale,
                  come previsto dall'informativa. Da quel momento il sistema
                  rifiuterà ogni elaborazione che riguardi i suoi dati.
                </p>
                <div className="sp-opp-azioni">
                  <button onClick={() => setOpposizione(false)}>Annulla</button>
                  <button className="sp-opp-conferma" onClick={async () => {
                    try {
                      await api.post(`/ai/opposizione/${paziente.id}`, { opposto: true });
                      setOpposizione(false);
                      loadPaziente();
                    } catch (e) {
                      notificaErrore(messaggioDa(e, 'Non riesco a registrare l\'opposizione'));
                    }
                  }}>
                    Registra l'opposizione
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Dove firma il paziente: stesso meccanismo dei consensi di
          procedura, con l'elenco dei tablet disponibili. */}
      {/* Anche qui faPratiche, e non solo sui pulsanti che ci portano: una
          strada nuova che apre la pratica senza guardarlo trova la finestra
          chiusa invece di far firmare il paziente per un 403. */}
      {daVerificare === 'scelta' && faPratiche && (
        <SceltaFirma
          pazienteId={paziente.id}
          consensi={[]}
          onSuPc={() => setDaVerificare('pc')}
          onCompletato={() => { setDaVerificare('no'); loadPaziente(); setVersioneConsensi((v) => v + 1); }}
          onAnnulla={() => setDaVerificare('no')}
        />
      )}

      {daVerificare === 'pc' && faPratiche && (
        <ModulisticaWizard
          modalita={paziente.tipo_accettazione === 'prelievi' ? 'prelievi' : 'completo'}
          pazienteEsistente={paziente}
          onClose={() => setDaVerificare('no')}
          onSuccess={() => { setDaVerificare('no'); loadPaziente(); setVersioneConsensi((v) => v + 1); }}
        />
      )}

      {/* «Scatta» sta nella barra in basso del telefono (variante B): qui
          resta «Carica», e la scheda dichiara il suo «Scatta» alla barra.
          La visita aperta (?vista=procedure&visita=…) e' la destinazione, se
          il server dice che e' di oggi e del medico (CaricaDalTelefono,
          destinazioneDa). */}
      {caricaDalTelefono && (
        <CaricaDalTelefono pazienteId={id} barra="carica" ascoltaScatta
                           visitaAperta={schermata === 'procedure' ? (Number(parametri.get('visita')) || null) : null} />
      )}

    </div>
  );
};

const Riga = ({ label, value, evidenzia }) => (
  <div className={`sp-row${evidenzia ? ' sp-row-note' : ''}`}>
    <span className="sp-row-label">{label}</span>
    <span className="sp-row-value">{value || 'N/A'}</span>
  </div>
);

export default PazienteDetail;
